import React, { useState, useEffect } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { IconButton, Icon, Grid, MenuItem, TextField } from '@material-ui/core';
import styled from 'styled-components';
import FormTextField from '../FormTextField';
import { FILTER_TYPES } from 'constants/filters';
import FormFormattedField from '../FormFormattedField';

const StyledGridWithFullWidth = styled(Grid)`
  width: 100%;
`;

const StyledIcon = styled(IconButton)`
  padding: 0;
  margin-bottom: 5px;
  > span {
    padding-bottom: 0;
    margin-right: 0;
  }
`;

const StyledOperatorField = styled(Field)`
  width: 100px;
`;

type FilterType = typeof FILTER_TYPES[keyof typeof FILTER_TYPES];

interface Filter {
  name: string;
  text: string;
  type: FilterType;
  operators?: boolean;
}

interface AdditionalFiltersProps {
  filters: Filter[];
  onNewAdditionalFilter?: () => void;
}

interface FilterState {
  selectedFilters: Filter[];
  unselectedFilters: Filter[];
}

const initAdditionalFilters = (filters: Filter[], formValues: Record<string, any>): FilterState => {
  let selectedFilters: Filter[] = [];
  let unselectedFilters: Filter[] = filters;

  filters.forEach(filter => {
    if (formValues && formValues[filter.name]) {
      selectedFilters = selectedFilters.concat(filter);
      unselectedFilters = unselectedFilters.filter(item => item.name !== filter.name);
    }
  });

  return { selectedFilters, unselectedFilters };
};

export const AdditionalFinalFormFilters: React.FC<AdditionalFiltersProps> = ({ filters, onNewAdditionalFilter }) => {
  const form = useForm();
  const formState = useFormState();
  const formValues = formState.values || {};

  const [unselectedFilters, setUnselectedFilters] = useState<Filter[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([]);

  // Initialize filters only on mount
  useEffect(() => {
    const { selectedFilters: selected, unselectedFilters: unselected } = initAdditionalFilters(filters, formValues);
    setSelectedFilters(selected);
    setUnselectedFilters(unselected);
  }, [filters]);

  const addAdditionalFilter = (additionalFilterName: string): void => {
    const selectedFilter = filters.find(filter => filter.name === additionalFilterName);
    if (selectedFilter) {
      setSelectedFilters(prev => [...prev, selectedFilter]);
      setUnselectedFilters(prev => prev.filter(filter => filter.name !== additionalFilterName));
    }
    if (onNewAdditionalFilter) {
      onNewAdditionalFilter();
    }
  };

  const removeAdditionalFilter = (name: string): void => {
    form.change(name, undefined);
    const unselectedFilter = filters.find(filter => filter.name === name);
    if (unselectedFilter) {
      setSelectedFilters(prev => prev.filter(filter => filter.name !== name));
      setUnselectedFilters(prev => [...prev, unselectedFilter]);
    }
  };

  return (
    <>
      {unselectedFilters.length !== 0 && (
        <TextField
          select
          label="Additional Filters"
          onChange={({ target }) => addAdditionalFilter(target.value)}
          value=""
        >
          {unselectedFilters.map(({ name, text }) => (
            <MenuItem key={name} value={name}>
              {text}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Grid container direction="column" spacing={8}>
        {selectedFilters.map(({ name, operators, text, type }, index) => (
          <Grid item xs={12} key={index}>
            <Grid container wrap="nowrap" direction="row" alignItems="flex-end" spacing={8}>
              {operators && (
                <Grid item>
                  <StyledOperatorField component={FormTextField} name={`${name}_operator`} label="Operator" select>
                    <MenuItem value="eq">Equals</MenuItem>
                    <MenuItem value="gt">Greater Than</MenuItem>
                    <MenuItem value="lt">Less Than</MenuItem>
                  </StyledOperatorField>
                </Grid>
              )}
              <StyledGridWithFullWidth item>
                <Field
                  component={type === FILTER_TYPES.STRING ? FormTextField : (FormFormattedField as any)}
                  name={name}
                  label={text}
                  fullWidth
                  allowNegative={false}
                  fixedDecimalScale
                  decimalScale={0}
                />
              </StyledGridWithFullWidth>
              <Grid item>
                <StyledIcon onClick={() => removeAdditionalFilter(name)}>
                  <Icon>cancel</Icon>
                </StyledIcon>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </>
  );
};
